/**
 * What a push notification asks the app to do — read, and turned into a route.
 *
 * Kept pure and on its own so the two decisions here can be tested without a
 * router, a store or a device: what a tap is asking for, and where in the app
 * that lands. The half that actually connects to the hub is
 * `services/pushDeepLink.ts`.
 *
 * ── Why the payload names a hub and not a URL ────────────────────────────────
 * The cloud that delivers a push knows the hub's id; it does NOT know whether
 * the person is standing in the house. Only the app can answer that — it holds
 * the addresses that answered on this network (`services/serverProfiles.ts`) and
 * the connect ladder that tries them before the tunnel
 * (`services/hubConnect.ts` → `connectResolvingRoute`). So a URL baked in the
 * cloud would be a guess that is wrong half the time, and it would have to be
 * re-guessed on the hub's own network. The payload carries `hubId` + a
 * hub-relative `path`, and the ROUTE is built here.
 */

/** What one tapped notification asks for. */
export interface PushTarget {
  /**
   * The hub that sent it. Either spelling the app holds is accepted — the
   * cloud's row id or the hub's own — because the account's hub list can be
   * looked up by either (`services/accountHubs.ts` → `findAccountHubRow`).
   */
  hubId: string;
  /** Hub-relative page to open once connected; absent means the hub's home. */
  path?: string;
}

/**
 * Read a push target out of whatever the transport handed over.
 *
 * Both transports are read here on purpose. A native message arrives as FCM
 * `data` (`{ hubId, path }`); a Web Push payload puts the same two fields
 * beside its `url`. One reader means the two can never drift into two different
 * ideas of where a tap goes — and a payload from an older or newer cloud, which
 * carries one field this build does not know, is simply read as far as it goes.
 *
 * Null means "not a hub notification": the push names no hub, so there is
 * nothing to connect to and the caller leaves the tap alone rather than guessing.
 */
export function readPushTarget(raw: unknown): PushTarget | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const bag = raw as Record<string, unknown>;
  const hubId = typeof bag.hubId === 'string' ? bag.hubId.trim() : '';
  if (!hubId) return null;

  const path = typeof bag.path === 'string' ? bag.path.trim() : '';
  return path ? { hubId, path } : { hubId };
}

/**
 * The app route for one notification: the hub, then the page inside it.
 *
 * `/h/<hubId>` alone is the hub's home — the router already redirects it to that
 * hub's dashboards — so a type with no obvious page still lands the person on
 * the right hub instead of nowhere. The path is normalised rather than trusted:
 * the cloud sends it without a leading slash, and a doubled one would be a
 * different route.
 */
export function hubRoute(hubId: string, path?: string): string {
  const id = hubId.trim();
  const suffix = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  return `/h/${id}${suffix}`;
}

/**
 * The query marker a browser push lands with, so the app knows to CONNECT.
 *
 * A service worker can only open a URL, and `/h/<hubId>/devices` on its own
 * renders the hub's shell — the router guard sends a window with no session for
 * that hub to the Local page and waits to be told. Arriving from a notification
 * is the one case where the app should not wait: the person already said yes by
 * tapping. The marker is how the arriving window tells the two apart.
 */
export const PUSH_ARRIVAL_PARAM = 'push';

/** True when this route is a window that arrived from a tap and should connect. */
export function isPushArrival(query: Record<string, unknown>): boolean {
  const marker = query[PUSH_ARRIVAL_PARAM];
  return marker === '1' || marker === 'true';
}
